import { appInject, appInjectable } from "@core/di/utils";
import { BaseService } from "@core/services/base";
import { DI_TOKENS } from "@shared/constants/di";
import { IConfigService } from "@shared/interfaces/config-service.interface";
import { IHttpClientService } from "@shared/interfaces/http-client-service.interface";
import { ICampaignsService } from "@shared/interfaces/campaigns-service.interface";
import {
  CampaignCpaVersionDto,
  CampaignDto,
  CampaignsListDto,
  DailyAffiliateRevenueRowDto,
  ListCampaignsDto,
} from "@shared/types/campaigns/campaign.dto";

@appInjectable()
export class CampaignsService extends BaseService implements ICampaignsService {
  private configService = appInject<IConfigService>(DI_TOKENS.configService);
  private httpClient = appInject<IHttpClientService>(
    DI_TOKENS.httpClientService,
  );
  private baseURL: string;

  constructor() {
    super();
    this.baseURL = this.configService.baseUrl;
  }

  list: ICampaignsService["list"] = async (params?: ListCampaignsDto) => {
    const { data } = await this.httpClient.get<{
      data: CampaignsListDto;
    }>("/campaigns", {
      params: {
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 20,
        query: params?.query,
      },
      baseURL: this.baseURL,
    });

    return (
      data.data ?? {
        campaigns: [],
        total: 0,
        page: 1,
        pageSize: 20,
        hasNext: false,
        hasPrevious: false,
      }
    );
  };

  create: ICampaignsService["create"] = async (body) => {
    const { data } = await this.httpClient.post<{ data: { campaign: CampaignDto } }>(
      "/campaigns",
      body,
      { baseURL: this.baseURL },
    );
    return data.data.campaign;
  };

  update: ICampaignsService["update"] = async (id, body) => {
    const { data } = await this.httpClient.patch<{ data: { campaign: CampaignDto } }>(
      `/campaigns/${id}`,
      body,
      { baseURL: this.baseURL },
    );
    return data.data.campaign;
  };

  delete: ICampaignsService["delete"] = async (id) => {
    await this.httpClient.delete(`/campaigns/${id}`, { baseURL: this.baseURL });
  };

  listCpaVersions: ICampaignsService["listCpaVersions"] = async (campaignId) => {
    const { data } = await this.httpClient.get<{
      data: { cpaVersions: CampaignCpaVersionDto[] };
    }>(`/campaigns/${campaignId}/cpa-versions`, { baseURL: this.baseURL });
    return data.data.cpaVersions;
  };

  addCpaVersion: ICampaignsService["addCpaVersion"] = async (
    campaignId,
    body,
  ) => {
    const { data } = await this.httpClient.post<{
      data: { cpaVersion: CampaignCpaVersionDto };
    }>(`/campaigns/${campaignId}/cpa-versions`, body, { baseURL: this.baseURL });
    return data.data.cpaVersion;
  };

  getDailyAffiliateRevenue: ICampaignsService["getDailyAffiliateRevenue"] =
    async (params) => {
      const { data } = await this.httpClient.get<{
        data: { dailyRevenue: DailyAffiliateRevenueRowDto[] };
      }>("/campaigns/daily-revenue", {
        params,
        baseURL: this.baseURL,
      });
      return data.data.dailyRevenue;
    };
}
